<template>
    <div class="editor-wrapper">
        <elem-input-object :name="name" :title="title" :required="required" :getValue="getEditorData"></elem-input-object>
        <textarea :id="'editor-' + id"></textarea>
    </div>
</template>

<script lang="ts">
import Component, { ComponentMethods } from "@/module/component/component"

import MessageController from "@/module/interactive/message"
import Request from "@/module/request/request"
import elemInputObject from "./elem-input-object.vue"
import Utils from "@/module/utils/utils"
import heic2any from "heic2any"

class ElemRichTextComponent extends ComponentMethods implements ComponentEntity {
    data() {
        return {
            id: Utils.getUuid(),
        }
    }

    props = {
        name: String,
        title: String,
        required: {
            type: Boolean,
            default: true,
        },
        value: {
            type: String,
            default: "",
        },
    }

    components = {
        elemInputObject,
    }

    async mounted() {
        this.initEditor()
    }

    methods = {
        isHeicLike(filename: string, mimeType?: string): boolean {
            const type = (mimeType || "").toLowerCase()
            const ext = (filename || "").split(".").pop()?.toLowerCase()

            return (
                type.includes("heic") ||
                type.includes("heif") ||
                ext === "heic" ||
                ext === "heif" ||
                ext === "heics" ||
                ext === "heifs"
            )
        },

        renameToPng(name: string): string {
            const base = (name || "").replace(/\.[^/.]+$/, "")
            return `${base || "image"}.png`
        },

        async convertHeicBlobToPngFile(blob: Blob, filename: string): Promise<File> {
            try {
                const converted = await heic2any({ blob, toType: "image/png" })
                const pngBlob = converted instanceof Blob ? converted : converted[0]
                return new File([pngBlob], this.renameToPng(filename), { type: "image/png" })
            } catch (err) {
                // fallback：若浏览器本身支持解码 HEIC（如 macOS Safari），用 canvas 转 png
                const pngBlob = await this.convertHeicToPngByBrowser(blob)
                return new File([pngBlob], this.renameToPng(filename), { type: "image/png" })
            }
        },

        convertHeicToPngByBrowser(blob: Blob): Promise<Blob> {
            return new Promise((resolve, reject) => {
                const url = URL.createObjectURL(blob)
                const img = new Image()

                const cleanup = () => {
                    URL.revokeObjectURL(url)
                }

                img.onload = () => {
                    try {
                        const canvas = document.createElement("canvas")
                        canvas.width = img.naturalWidth || img.width
                        canvas.height = img.naturalHeight || img.height

                        const ctx = canvas.getContext("2d")
                        if (!ctx) {
                            cleanup()
                            return reject(new Error("Canvas 初始化失败"))
                        }

                        ctx.drawImage(img, 0, 0)

                        canvas.toBlob(
                            outBlob => {
                                cleanup()
                                if (!outBlob) return reject(new Error("PNG 生成失败"))
                                resolve(outBlob)
                            },
                            "image/png",
                            1
                        )
                    } catch (e) {
                        cleanup()
                        reject(e)
                    }
                }

                img.onerror = () => {
                    cleanup()
                    reject(new Error("当前浏览器不支持 HEIC 解码"))
                }

                img.src = url
            })
        },

        getEditorData() {
            const editor = (window as any).tinymce.get("editor-" + this.id)
            return editor ? editor.getContent() : ""
        },

        setEditorData(html: string) {
            const editor = (window as any).tinymce.get("editor-" + this.id)
            if (editor) editor.setContent(html)
        },

        initEditor() {
            this.uploadQueue = Promise.resolve();

            // 初始化 TinyMCE
            (window as any).tinymce.init({
                selector: "#editor-" + this.id,
                language: "zh-Hans",
                license_key: "gpl",
                height: 600,
                menubar: true,
                branding: false,
                promotion: false,
                relative_urls: false,
                remove_script_host: false,
                automatic_uploads: true,
                images_file_types: "jpeg,jpg,jpe,jfi,jif,jfif,png,gif,bmp,webp,heic,heif,heics,heifs",
                plugins: [
                    "advlist",
                    "autolink",
                    "lists",
                    "link",
                    "image",
                    "charmap",
                    "preview",
                    "anchor",
                    "searchreplace",
                    "visualblocks",
                    "code",
                    "fullscreen",
                    "insertdatetime",
                    "media",
                    "table",
                    "help",
                    "wordcount",
                    "emoticons",
                    "autosave",
                    "quickbars",
                    "codesample",
                ], // 启用的插件列表
                toolbar: [
                    "code formatselect fontselect fontsizeselect forecolor backcolor bold italic underline strikethrough link alignment outdent indent bullist numlist blockquote subscript superscript removeformat table image media importword charmap pagebreak formatpainter cut copy undo redo restoredraft searchreplace",
                ], // 工具栏按钮列表

                image_dimensions: false,
                media_dimensions: false,
                content_style: `
                    img,video {
                        width: 100% !important;
                        max-width: 100% !important;
                        height: auto !important;
                        display: block;
                    }
                    .mce-preview-object {
                        width: 100% !important;
                    }
                `,

                // ✅ 图片上传逻辑
                images_upload_handler: (blobInfo) => {
                    const uploadTask = this.uploadQueue.then(async () => {
                        const formData = new FormData()

                        const originalBlob = blobInfo.blob()
                        const originalName = blobInfo.filename()
                        const isHeic = this.isHeicLike(originalName, originalBlob?.type)

                        if (isHeic) {
                            try {
                                const pngFile = await this.convertHeicBlobToPngFile(originalBlob, originalName)
                                formData.append("file", pngFile, pngFile.name)
                            } catch (e) {
                                // 转换失败时回退：上传原始 HEIC（由后端处理/或直接存储）
                                formData.append("file", originalBlob, originalName)
                            }
                        } else {
                            formData.append("file", originalBlob, originalName)
                        }

                        const res = await Request.post<obj>("WEB://Resource/Image/Upload", formData, {
                            onFail: function (err) {
                                MessageController.error(JSON.parse(err.responseText).message)
                                return false
                            },
                        })

                        return res.url
                    })

                    this.uploadQueue = uploadTask.catch(() => {})
                    return uploadTask
                },

                // 视频上传
                file_picker_types: "image media",
                file_picker_callback: (callback: any, value: any, meta: any) => {
                    if (meta.filetype === "image") {
                        const input = document.createElement("input")
                        input.type = "file"
                        input.accept = "image/*,.heic,.heif,.heics,.heifs"

                        input.onchange = async () => {
                            const file = input.files?.[0]
                            if (!file) return

                            const isHeic = this.isHeicLike(file.name, file.type)

                            let uploadFile: File | Blob = file
                            let uploadName: string = file.name

                            if (isHeic) {
                                try {
                                    const pngFile = await this.convertHeicBlobToPngFile(file, file.name)
                                    uploadFile = pngFile
                                    uploadName = pngFile.name
                                } catch (e) {
                                    uploadFile = file
                                    uploadName = file.name
                                }
                            }

                            const formData = new FormData()
                            formData.append("file", uploadFile, uploadName)

                            const res = await Request.post<obj>("WEB://Resource/Image/Upload", formData, {
                                onFail: function (err) {
                                    MessageController.error(JSON.parse(err.responseText).message)
                                    return false
                                },
                            })

                            callback(res.url, { alt: uploadName })
                        }

                        input.click()
                    }

                    if (meta.filetype === "media") {
                        const input = document.createElement("input")
                        input.type = "file"
                        input.accept = "video/*"

                        input.onchange = async () => {
                            const file = input.files?.[0]
                            if (!file) return

                            const formData = new FormData()
                            formData.append("file", file)

                            // 上传到后端接口
                            const res = await Request.post<obj>("WEB://Resource/Video/Upload", formData, {
                                onFail: function (err) {
                                    MessageController.error(JSON.parse(err.responseText).message)
                                    return false
                                },
                            })

                            // 回调插入视频
                            callback(res.url, { source2: res.url, poster: "" })
                        }

                        input.click()
                    }
                },
                setup: (editor: any) => {
                    editor.on("init", () => {
                        // 初始化完成后写入内容
                        if (this.value) {
                            editor.setContent(this.value)
                        }
                    })
                },
            })
        },
    }
}

export default Component.build(new ElemRichTextComponent())
</script>

<style>
.editor-wrapper {
    width: 100%;
    max-width: none;
}
#editor {
    width: 100%;
    max-width: none;
}
</style>
